// src/encounter/intelligence/explain-card.tsx
//
// The Explain card (Scrips Intelligence rail), bound to ExplanationV1. It draws what the engine sent and the fixed lines
// the encounter owner ruled; it writes no clinical sentence of its own (see explanation.ts for every source of text).
//
// Geometry: the two-line result row (rail session decision, 31 Oct, after the 18f one-line row did not fit 300-416px
// cards). Line 1: test name (flexible, word boundaries only) · date (one line, right-aligned) · source marker (w-5).
// Line 2: value (w-22) · Lab flag (w-10) · Ref range (flexible) · trend (w-12, 2+ points only), under a header over line
// 2 only. Fixed widths keep line-2 cells and dates aligned across rows. Styling is Signal DS classes in className only: the
// Practitioner App ships no stylesheet and no inline style (check-no-css, check-no-inline-styles,
// check-no-arbitrary-classes). A true fixed-track CSS grid needs a DS grid-template utility; see the C map below. Wording: round 18e C, T1-T11, D-5275 (trends), D-5276 (R1, X5), D-5278 (O1, O2).
// Read-only: the card has no verb that writes, orders or signs. Nothing here logs.
import { Fragment, useId, useState, type ReactNode } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';
import {
  STATE_LINE,
  VERDICT_WORD,
  WORDS,
  answerView,
  criticalRowsOf,
  inputsOf,
  labOf,
  originOf,
  d45,
  type ExplanationV1,
  type ProblemRowView,
  type ResultRowView,
  type Source,
} from './explanation';

import { C } from './explain-card-classes';
import { RAIL_WORDS } from './rail-words';

export interface ExplainCardProps {
  explanation: ExplanationV1;
  /** Round 18r "Open the results list" (under the results): opens the patient's results. Absent: not drawn. */
  onOpenResults?: () => void;
}


/** One source marker per row (design 3): "Source: <laboratory>, <date>", opening that row's own record item. Round 18r:
 *  a laboratory result carries the lab glyph, any other record item (the problem list) the folder glyph. */
function SourceMarker({ label, open, onToggle, controls, kind = 'lab' }: { label: string; open: boolean; onToggle: () => void; controls: string; kind?: 'lab' | 'record' }) {
  return (
    <button type="button" className={C.marker} aria-label={label} title={label} aria-expanded={open} aria-controls={open ? controls : undefined} onClick={onToggle} data-source={kind}>
      <Icon name={kind === 'lab' ? 'lab' : 'folder'} size={12} aria-hidden />
    </button>
  );
}

function valueOf(s: Source): string {
  return `${s.value ?? ''}${s.unit ? ` ${s.unit}` : ''}`;
}

/** A5: the record item behind a row, and the row's verbatim engine sentence (design 1: it opens on tap). */
function RecordItem({ id, sentence, source, observations }: { id: string; sentence: string; source: Source; observations?: string[] }) {
  return (
    <div id={id} className={C.details} data-slot="explain-record-item">
      <p>{sentence}</p>
      {source.printedFlag ? <p>{`Laboratory flag: ${source.printedFlag}`}</p> : null}
      <p>{`Record item: ${source.recordId}`}</p>
      {observations?.length ? (
        // L8: every observation of the trend's series, plotted or not, keyboard reachable with the marker.
        <ul className={C.lines} aria-label="Results in this series">
          {observations.map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A calendar-spaced micro trend (L4): x by collection date, y by value; no band, no fit, neutral points. The DS
 *  Sparkline spaces points by index, which L4 forbids, so this draws the same 48 x 12 mark with dates. */
function TrendMark({ points }: { points: Array<{ day: number; value: number }> }) {
  const W = 48;
  const H = 12;
  const pad = 2;
  const d0 = points[0].day;
  const d1 = points[points.length - 1].day;
  const lo = Math.min(...points.map((p) => p.value));
  const hi = Math.max(...points.map((p) => p.value));
  const x = (d: number) => (d1 === d0 ? W / 2 : pad + ((d - d0) / (d1 - d0)) * (W - 2 * pad));
  const y = (v: number) => (hi === lo ? H / 2 : H - pad - ((v - lo) / (hi - lo)) * (H - 2 * pad));
  const xy = points.map((p) => `${x(p.day).toFixed(2)},${y(p.value).toFixed(2)}`);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden data-slot="explain-trend-svg">
      <polyline points={xy.join(' ')} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      {xy.map((p) => {
        const [cx, cy] = p.split(',');
        return <circle key={p} cx={cx} cy={cy} r={1.5} fill="currentColor" />;
      })}
    </svg>
  );
}

function FlagCell({ row }: { row: ResultRowView }) {
  const f = row.flag;
  if (f.kind === 'blank') {
    return (
      <span className={C.flag} data-flag-kind="blank" data-flag>
        <span className="sr-only">{`${WORDS.flagHeader}: ${f.aria}`}</span>
      </span>
    );
  }
  if (f.kind === 'critical') {
    // T2: bold, error colour and an icon, never colour alone; the visible "Critical" tag sits under the row.
    return (
      <span className={C.flagCritical} data-flag-kind="critical" data-flag>
        <span data-slot="explain-flag-icon" aria-hidden>
          <Icon name="flag" size={12} aria-hidden />
        </span>
        <span aria-hidden>{f.text}</span>
        <span className="sr-only">{`${WORDS.flagHeader}: ${f.aria}`}</span>
      </span>
    );
  }
  // H, L, A and qualitative codes: the code verbatim, bold ink, no icon, no colour (D-5275 P1). Others: plain.
  return (
    <span className={f.kind === 'plain' ? C.flagPlain : C.flag} data-flag-kind={f.kind} data-flag>
      <span aria-hidden>{f.text}</span>
      <span className="sr-only">{`${WORDS.flagHeader}: ${f.aria}`}</span>
    </span>
  );
}

function RangeCell({ row, open, onToggle, controls }: { row: ResultRowView; open: boolean; onToggle: () => void; controls: string }) {
  const r = row.range;
  if (r.kind === 'plain')
    return (
      <span className={C.range}>
        <span className="sr-only">{`${WORDS.rangeHeader}: `}</span>
        {r.text}
      </span>
    );
  if (r.kind === 'none')
    return (
      <span className={C.range}>
        <span className="sr-only">no reference range came with this result</span>
      </span>
    );
  return (
    <span className={C.range}>
      <button type="button" className={C.linkButton} aria-expanded={open} aria-controls={open ? controls : undefined} onClick={onToggle}>
        {WORDS.seeReport}
      </button>
    </span>
  );
}

/** Line 2 of a row or sub-row: value · Lab flag · Ref range · trend. The trend cell is always laid out (empty when the
 *  result has no series of two or more points), so every row's columns line up (round 18r grid: 88 · 40 · 1fr · 48). */
function Line2({ row, open, onToggle, controls }: { row: ResultRowView; open: boolean; onToggle: () => void; controls: string }) {
  return (
    <div className={C.line2}>
      <span className={row.flag.kind === 'critical' ? C.valueCritical : C.value} data-value>
        <span className="sr-only">Value: </span>
        {valueOf(row.source)}
      </span>
      <FlagCell row={row} />
      <RangeCell row={row} open={open} onToggle={onToggle} controls={controls} />
      <span className={C.spark}>
        {row.trend ? (
          // The mark is aria-hidden; the wrapper carries the chart's accessible name (L8).
          <span role="img" aria-label={row.trend.label} data-slot="explain-trend">
            <TrendMark points={row.trend.points} />
          </span>
        ) : null}
      </span>
    </div>
  );
}

/** Engine c52fe7c: a series whose reference range changed over time (printedInterval "") draws its trend with no band
 *  (the trend never has one, L4) and the engine's rangeNote, verbatim, under it at the 12px meta size. */
function TrendRangeNote({ row }: { row: ResultRowView }) {
  if (!row.trend?.rangeNote) return null;
  return (
    <p className={C.meta} data-slot="explain-trend-range-note">
      {row.trend.rangeNote}
    </p>
  );
}

/** The date cell: "<date>" then " · <status tag>" (T11), one line, right-aligned. */
function DateCell({ row }: { row: ResultRowView }) {
  return (
    <span className={C.date} data-date>
      {row.date}
      {row.statusTag ? (
        <>
          {' · '}
          <span aria-label={row.statusAria}>{row.statusTag}</span>
        </>
      ) : null}
    </span>
  );
}

function RowDetails({ row, uid, open }: { row: ResultRowView; uid: string; open: 'source' | 'range' | null }) {
  if (open === 'source') return <RecordItem id={`${uid}-src`} sentence={row.sentence} source={row.source} observations={row.trend?.observations} />;
  if (open === 'range')
    return (
      <div id={`${uid}-range`} className={C.details} data-slot="explain-range-text">
        {row.range.kind === 'report' && row.range.text ? <p>{row.range.text}</p> : null}
        <p>{WORDS.rangeNote}</p>
      </div>
    );
  return null;
}

function ResultRow({ row, groupStart, first }: { row: ResultRowView; groupStart: boolean; first: boolean }) {
  const uid = useId();
  const [open, setOpen] = useState<'source' | 'range' | null>(null);
  const s = row.source;
  const lab = labOf(s.lab);
  const critical = row.flag.kind === 'critical';
  const toggle = (what: 'source' | 'range') => setOpen((o) => (o === what ? null : what));
  return (
    <div
      role="listitem"
      aria-label={row.sentence}
      className={[C.row, 'group', groupStart && !first ? C.rowGroupStart : ''].filter(Boolean).join(' ')}
      data-slot="explain-row"
      data-flag-class={s.flagClass ?? 'blank'}
      data-group-start={groupStart ? '' : undefined}
      data-first={first ? '' : undefined}
      data-most-recent={row.mostRecent ? '' : undefined}
    >
      {/* LINE 1: test name (wraps at word boundaries only) · date (one line, right-aligned) · source marker. */}
      <div className={C.line1}>
        <span className={C.name} data-name>
          {s.name}
        </span>
        <DateCell row={row} />
        <span className={C.markerCell}>
          <SourceMarker label={`Source: ${lab}, ${row.date}`} open={open === 'source'} onToggle={() => toggle('source')} controls={`${uid}-src`} />
        </span>
      </div>
      <Line2 row={row} open={open === 'range'} onToggle={() => toggle('range')} controls={`${uid}-range`} />
      <TrendRangeNote row={row} />
      {/* At most one sub-line per row (craft review): critical tag · Most recent on file · <lab> · the R2-bis marker. A
          "Most recent on file" row reaches here only when it has no older flagged row to sit under. */}
      <Subline critical={critical} mostRecent={row.mostRecent ? `${WORDS.mostRecent} · ${lab}` : null} outside={row.outside} />
      <RowDetails row={row} uid={uid} open={open} />
    </div>
  );
}

/** A row's (or sub-row's) one sub-line: the critical tag (T2) · "Most recent on file · <lab>" · the R2-bis marker. */
function Subline({ critical, mostRecent = null, outside }: { critical: boolean; mostRecent?: string | null; outside: boolean }) {
  if (!critical && !mostRecent && !outside) return null;
  return (
    <span className={C.subline} data-slot="explain-subline">
      {[
        critical ? (
          <span key="c" className={C.subnoteCritical} data-critical>
            {WORDS.criticalTag}
          </span>
        ) : null,
        mostRecent ? <span key="m">{mostRecent}</span> : null,
        outside ? <span key="o">{WORDS.outsideMarker}</span> : null,
      ]
        .filter(Boolean)
        .flatMap((node, i) => (i === 0 ? [node] : [<span key={`s${i}`}>{' · '}</span>, node]))}
    </span>
  );
}

/** Ruling 1 Nov ("duplicate newest rows on Explain"): the newest result, ONCE, as an indented sub-row directly under its
 *  older flagged row. "Most recent on file · <laboratory>", then the same cells as every row, each from the cited source
 *  item; the R2-bis marker when the engine compared it. Its accessible name is the engine's T8 sentence, verbatim. */
function SubRow({ row }: { row: ResultRowView }) {
  const uid = useId();
  const [open, setOpen] = useState<'source' | 'range' | null>(null);
  const lab = labOf(row.source.lab);
  const critical = row.flag.kind === 'critical';
  const toggle = (what: 'source' | 'range') => setOpen((o) => (o === what ? null : what));
  return (
    <div role="listitem" aria-label={row.sentence} className={C.subRow} data-slot="explain-subrow" data-flag-class={row.source.flagClass ?? 'blank'}>
      <span aria-hidden className={C.subRule} data-slot="explain-subrow-rule" />
      <div className={C.line1}>
        <span className={C.subName} data-name>{`${WORDS.mostRecent} · ${lab}`}</span>
        <DateCell row={row} />
        <span className={C.markerCell}>
          <SourceMarker label={`Source: ${lab}, ${row.date}`} open={open === 'source'} onToggle={() => toggle('source')} controls={`${uid}-src`} />
        </span>
      </div>
      <Line2 row={row} open={open === 'range'} onToggle={() => toggle('range')} controls={`${uid}-range`} />
      <TrendRangeNote row={row} />
      {/* Codex P2 (clinical safety): a critical newest result keeps the critical tag here exactly as ResultRow draws it;
          the critical flag cell and value style come from Line2, the same cells as every row. */}
      <Subline critical={critical} outside={row.outside} />
      <RowDetails row={row} uid={uid} open={open} />
    </div>
  );
}

const groupOf = (r: ResultRowView) => {
  const c = r.source.flagClass ?? 'blank';
  if (c === 'critical') return 0;
  if (c === 'not-classified' || c === 'abnormal' || c === 'qualitative') return 1;
  return 2;
};

/** One grid for every result row of the card (round 17), with the "Lab flag" and "Ref range" headers (T3, T5). */
function ResultsGrid({ rows }: { rows: ResultRowView[] }) {
  if (!rows.length) return null;
  return (
    <div className={C.results} data-slot="explain-results">
      {/* Round 18r column header over line 2: "Value · Lab flag · Ref range", in the cells' own columns. */}
      <div className={C.header} aria-hidden data-slot="explain-col-header">
        <span className="w-22 shrink-0">Value</span>
        <span className="w-10 shrink-0">{WORDS.flagHeader}</span>
        <span className="flex-1 min-w-0">{WORDS.rangeHeader}</span>
        <span className="w-12 shrink-0" />
      </div>
      <div role="list" className={C.rows}>
        {rows.map((r, i) => {
          const prev = rows[i - 1];
          const groupStart = !!prev && !r.mostRecent && groupOf(prev) !== groupOf(r);
          return (
            <Fragment key={`${r.source.recordId}-${i}`}>
              <ResultRow row={r} groupStart={groupStart} first={i === 0} />
              {r.subRow ? <SubRow row={r.subRow} /> : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** True only when nothing qualifies the entry: no verification status other than confirmed, no status other than active. */
function plainProblem(s: { verificationStatus?: string; clinicalStatus?: string }): boolean {
  const v = (s.verificationStatus ?? '').trim().toLowerCase(), c = (s.clinicalStatus ?? '').trim().toLowerCase();
  return (v === '' || v === 'confirmed') && (c === '' || c === 'active');
}

function ProblemRow({ row }: { row: ProblemRowView }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const s = row.source;
  // Round 17 section 3: the name, then the code as meta; the full sentence stays one tap away.
  return (
    <li aria-label={row.sentence} data-slot="explain-problem">
      <div className={C.problem}>
        {/* A qualified entry (refuted, provisional, inactive...) shows the engine sentence, never the bare name (Codex P2). */}
        <span className={C.problemName}>{s.code && plainProblem(s) ? s.name : row.sentence}</span>
        <span className={C.problemMeta}>
          {s.code ? <span className={C.problemCode}>{s.code}</span> : null}
          <SourceMarker label="Source: problem list" kind="record" open={open} onToggle={() => setOpen(!open)} controls={`${uid}-src`} />
        </span>
      </div>
      {open ? <RecordItem id={`${uid}-src`} sentence={row.sentence} source={s} /> : null}
    </li>
  );
}

/** The Limits row (D-5276 / round 18e C): the engine's at-rest lines, opening to the engine's full list. The card
 *  types no limits text of its own; the three group labels are the ruled list's own headings. */
function LimitsRow({ lines, full }: { lines: string[]; full?: ExplanationV1['limitsFull'] }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  if (!lines.length && !full) return null;
  // Round 18r: the whole Limits row is the control, the engine's at-rest lines then a chevron; with no at-rest line (a
  // withheld card) the control reads "Limits".
  const text = (
    <span className="flex-1 min-w-0 flex flex-col gap-1">
      {(lines.length ? lines : ['Limits']).map((n) => (
        <span key={n}>{n}</span>
      ))}
    </span>
  );
  return (
    <div data-slot="explain-limits">
      {full ? (
        <button type="button" className={C.limits} aria-expanded={open} aria-controls={open ? `${uid}-limits` : undefined} onClick={() => setOpen(!open)} data-limits>
          {text}
          <Icon name={open ? 'up' : 'down'} size={12} aria-hidden />
        </button>
      ) : (
        <div className={C.limitsStatic}>{text}</div>
      )}
      {open && full ? (
        <div id={`${uid}-limits`} className={C.details}>
          {(
            [
              ['Read', full.read],
              ['Shown, not used in the summary', full.shownNotUsed],
              ['Not read', full.notRead],
            ] as const
          ).map(([head, items]) => (
            <ul key={head} className={C.lines} aria-label={head}>
              {items.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** O2: the checks, one tap away and keyboard reachable, with the inputs, versions and output id (DoH §3.1.6). */
function HowProduced({ x }: { x: ExplanationV1 }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const inputs = inputsOf(x);
  return (
    <div data-slot="explain-how">
      <p className={C.meta} data-slot="explain-chrome" data-origin={x.origin?.kind}>
        {`${originOf(x)} · `}
        <button type="button" className={C.linkButton} aria-expanded={open} aria-controls={open ? `${uid}-how` : undefined} onClick={() => setOpen(!open)}>
          {WORDS.howProduced}
        </button>
      </p>
      {(x.readLines ?? []).map((l) => (
        <p key={l} className={C.meta}>
          {l}
        </p>
      ))}
      {/* D-5275 display line (read time, age, sex): shown, not used in the summary, so it sits with the Read lines (the
          design frame has no age/sex line of its own). Display only; never sent to any check. */}
      {x.display ? <DisplayHeader d={x.display} /> : null}
      {open ? (
        <div id={`${uid}-how`} className={C.details} data-slot="explain-how-panel">
          <ul className={C.checks} aria-label="Checks">
            {x.checks.map((c) => (
              <li key={c.id} className={C.check}>
                <span>{c.name}</span>
                <span>{c.state ?? VERDICT_WORD[c.verdict]}</span>
              </li>
            ))}
          </ul>
          {x.checksNote ? <p>{x.checksNote}</p> : null}
          {inputs.length ? (
            <ul className={C.lines} aria-label="Inputs">
              {inputs.map((s) => (
                <li key={s.recordId}>{[s.name, s.value !== undefined ? valueOf(s) : null, s.measuredOn ? d45(s.measuredOn) : null, s.recordId].filter(Boolean).join(' · ')}</li>
              ))}
            </ul>
          ) : null}
          <p>{`Versions: ${x.schema} · ${x.checkSet}`}</p>
          {x.produced ? <p>{`Rules: ${x.produced.rules.name} · ${x.produced.rules.version}`}</p> : null}
          {x.produced?.model ? <p>{`${x.produced.model.role}: ${x.produced.model.deployment}`}</p> : null}
          <p>{`Output id: ${x.requestId}`}</p>
        </div>
      ) : null}
    </div>
  );
}

/** H1/H2/A1 (D-5275): "Read <date>, <time> · <age> · <sex>", every value the engine's, display only. */
function DisplayHeader({ d }: { d: NonNullable<ExplanationV1['display']> }) {
  const age = 'years' in d.age ? `${d.age.years} y` : d.age.text;
  return (
    <p className={C.meta} data-slot="explain-display-header">
      {`Read ${d45(d.readAt.date)}, ${d.readAt.time} · ${age} · `}
      {'recorded' in d.sex ? <span aria-label="Administrative sex, as recorded">{d.sex.recorded}</span> : <span>{d.sex.text}</span>}
    </p>
  );
}

/** V1-V5 (D-5275): "Recorded at visits", display only. Each reading is one value as recorded (a blood pressure reading
 *  would be one value too), with its date, time, status tag and measurement source, all as the engine sends them. */
function RecordedAtVisits({ v }: { v: NonNullable<ExplanationV1['display']>['vitals'] }) {
  return (
    <section aria-label={v.label} data-slot="explain-visits" className="flex flex-col gap-1">
      <hr className={C.hairline} />
      <p className={C.count}>{v.label}</p>
      {v.status === 'unavailable' ? (
        <p className={C.meta}>{v.reason}</p>
      ) : (
        <>
          <ul className={C.lines}>
            {v.readings.map((r) => (
              <li key={r.sourceId} className="flex flex-col">
                <span className="flex items-baseline gap-2">
                  <span className={C.value}>{r.text}</span>
                  <span className={C.meta}>{[r.date ? d45(r.date) : null, r.time, r.tag || null].filter(Boolean).join(' · ')}</span>
                </span>
                <span className={C.subnote}>{r.measurementSource}</span>
              </li>
            ))}
          </ul>
          <p className={C.meta}>{v.window}</p>
          {/* Engine ece6f7b drops notYetShown once blood pressure is shown; absent means nothing to say. */}
          {v.notYetShown ? <p className={C.meta}>{v.notYetShown}</p> : null}
        </>
      )}
    </section>
  );
}

/** The read time, right-aligned on the title line (18f): the clinic's local clock, 24 h. */
function readTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function TitleRow({ x, title }: { x: ExplanationV1; title: string }) {
  const time = readTime(x.answeredAt);
  return (
    <div className={C.titleRow}>
      <h4 className={C.title}>{title}</h4>
      {time ? (
        <time className={C.meta} dateTime={x.answeredAt}>
          {time}
        </time>
      ) : null}
    </div>
  );
}

function Frame({ x, children, title }: { x: ExplanationV1; children: ReactNode; title: string }) {
  return (
    <article className={C.card} aria-label={title} data-slot="explain-card" data-state={x.state} data-reason={x.reason}>
      {children}
    </article>
  );
}

export function ExplainCard({ explanation: x, onOpenResults }: ExplainCardProps) {
  if (x.state === 'answered') {
    const v = answerView(x);
    return (
      <Frame x={x} title={v.title}>
        <TitleRow x={x} title={v.title} />
        {/* Round 18r order: headline and counts; the origin line, the Read lines (and the display line); the column
            header and the results; "Open the results list"; Problems; the not-found line; Limits. */}
        {v.headline ? <p className={C.count} data-slot="explain-headline">{v.headline}</p> : null}
        {v.countLines.map((t) => (
          <p key={t} className={C.count} data-slot="explain-count">
            {t}
          </p>
        ))}
        <HowProduced x={x} />
        <ResultsGrid rows={v.results} />
        {onOpenResults ? (
          <button type="button" className={C.openResults} data-action="open-results" onClick={onOpenResults}>
            {RAIL_WORDS.openResults}
          </button>
        ) : null}
        {v.resultNotes.length ? (
          <ul className={C.lines}>
            {v.resultNotes.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        ) : null}
        {v.problems.length ? (
          <>
            <p className={C.group} data-slot="explain-problems-label">
              {RAIL_WORDS.problemsLabel}
            </p>
            <ul className={C.lines} aria-label="Problem list">
              {v.problems.map((p) => (
                <ProblemRow key={p.source.recordId} row={p} />
              ))}
            </ul>
          </>
        ) : null}
        {v.gaps.length ? (
          <ul className={C.lines} data-slot="explain-gaps">
            {v.gaps.map((t) => (
              <li key={t} className={C.gap}>
                <span aria-hidden className={C.gapMark} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {x.display ? <RecordedAtVisits v={x.display.vitals} /> : null}
        <LimitsRow lines={v.limitNotes} full={x.limitsFull} />
      </Frame>
    );
  }

  // not-enabled: no card at all (R4-2). duplicate-request: the hook keeps the earlier card and never passes this one.
  if (x.reason === 'not-enabled' || x.reason === 'duplicate-request') return null;
  // Withheld: only the engine's own line, verbatim; none is written here (#825 rule 2a, H3).
  // Withheld: only the engine's line. Failed: the engine's line when it sends one (a check that could not run), else the
  // state copy for the refusal.
  const line = x.state === 'withheld' ? (x.withheldLine ?? null) : (x.withheldLine ?? (x.reason ? STATE_LINE[x.reason] : STATE_LINE.technical));
  const title = x.title ?? 'Scrips Intelligence';
  if (x.reason === 'consent-withdrawn') {
    return (
      <Frame x={x} title={title}>
        <p role="alert" className={C.stateLine}>
          {line}
        </p>
      </Frame>
    );
  }
  if (x.state === 'withheld' || (x.state === 'failed' && x.critical)) {
    // X5: a withheld card still shows its critical rows, each with its own source marker. Engine 23bd8ce: so does a
    // failed card whose AI check could not run (provider error, timeout); the panel offers Retry.
    return (
      <Frame x={x} title={title}>
        <HowProduced x={x} />
        {line ? (
          <p className={C.stateLine} data-slot="explain-state-line">
            {line}
          </p>
        ) : null}
        <ResultsGrid rows={criticalRowsOf(x)} />
        <LimitsRow lines={[]} full={x.limitsFull} />
      </Frame>
    );
  }
  return (
    <Frame x={x} title={title}>
      <p className={C.stateLine} data-slot="explain-state-line">
        {line}
      </p>
    </Frame>
  );
}
